import {
  useEffect,
  useRef,
  useState,
  type CSSProperties,
  type PointerEvent as ReactPointerEvent,
} from "react";
import { Loader2 } from "lucide-react";

import { Button } from "@/shared/ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/shared/ui/dialog";

/**
 * 裁剪上传(从老分支 `AiWebsitePage.tsx` 搬,A.53 / A.57):选图 → 选比例 → 拖动 / 缩放框 → 裁好交给 `upload`。
 * 改动只有三处:① 上传口由调用方给(`upload(base64) → 完整 url`,本组件不认识 service);② 裁剪结果长边压到
 * ≤ 1600px 的 JPEG(A.53);③ 四角光标用 style,不拼动态 Tailwind 类(拼出来的类不会被生成)。
 */

/** 宿主上传走 nginx(约 1MB 上限)且 base64 膨胀约 1/3:原始字节超这个数先压。 */
const UPLOAD_LIMIT = 650_000;
/** 裁剪后长边上限(A.53)。 */
export const CROP_MAX_SIDE = 1600;

function readAsDataUrl(file: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result));
    r.onerror = () => reject(new Error("读取文件失败"));
    r.readAsDataURL(file);
  });
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error("无法解析该图片(请用 JPG/PNG)"));
    img.src = src;
  });
}

const toJpeg = (canvas: HTMLCanvasElement, q: number) =>
  new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, "image/jpeg", q));

/** 去掉 dataURL 前缀的 base64(api 要裸 base64,A.53);超限再降质量。 */
async function toBase64Under(canvas: HTMLCanvasElement): Promise<string> {
  let blob = (await toJpeg(canvas, 0.92)) ?? (await toJpeg(canvas, 0.8));
  if (!blob) throw new Error("图片处理失败");
  for (const q of [0.8, 0.65, 0.5]) {
    if (blob.size <= UPLOAD_LIMIT) break;
    blob = (await toJpeg(canvas, q)) ?? blob;
  }
  const data = await readAsDataUrl(blob);
  return data.split(",")[1] ?? data;
}

type Rect = { x: number; y: number; w: number; h: number };
type Corner = "se" | "sw" | "ne" | "nw";

const clamp = (v: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, v));

/** 按比例在 W×H 内给出居中最大框;a=null 用整图。 */
function rectFor(W: number, H: number, a: number | null): Rect {
  if (!a) return { x: 0, y: 0, w: W, h: H };
  if (W / H > a) {
    const h = H;
    const w = Math.floor(h * a);
    return { x: Math.floor((W - w) / 2), y: 0, w, h };
  }
  const w = W;
  const h = Math.floor(w / a);
  return { x: 0, y: Math.floor((H - h) / 2), w, h };
}

const PRESETS: Array<{ label: string; value: number | null }> = [
  { label: "原图", value: null },
  { label: "16:9", value: 16 / 9 },
  { label: "4:3", value: 4 / 3 },
  { label: "3:2", value: 3 / 2 },
  { label: "1:1", value: 1 },
  { label: "3:4", value: 3 / 4 },
  { label: "2:3", value: 2 / 3 },
  { label: "9:16", value: 9 / 16 },
];

const CORNERS: ReadonlyArray<{ mode: Corner; pos: CSSProperties; cursor: string }> = [
  { mode: "se", pos: { right: -1.5, bottom: -1.5 }, cursor: "nwse-resize" },
  { mode: "ne", pos: { right: -1.5, top: -1.5 }, cursor: "nesw-resize" },
  { mode: "nw", pos: { left: -1.5, top: -1.5 }, cursor: "nwse-resize" },
  { mode: "sw", pos: { left: -1.5, bottom: -1.5 }, cursor: "nesw-resize" },
];

interface Drag {
  mode: "move" | Corner;
  sx: number;
  sy: number;
  orig: Rect;
  aspect: number | null;
  nat: { w: number; h: number };
  /** 缩放时固定的对侧角(自然坐标)。 */
  anchor?: { x: number; y: number };
  horiz?: "left" | "right";
  vert?: "top" | "bottom";
}

export function CropUploadDialog({
  open,
  onOpenChange,
  upload,
  onDone,
  defaultAspect,
  title = "裁剪后上传",
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  /** 裸 base64 → 上传后的完整 url;抛错就停在弹窗里显示原因。 */
  upload: (base64: string) => Promise<string>;
  onDone: (absoluteUrl: string) => void;
  /** 默认展示比例(首屏图 16:9 / Logo 1:1 / 其它 null = 原图)。 */
  defaultAspect?: number | null;
  title?: string;
}) {
  const pickRef = useRef<HTMLInputElement | null>(null);
  const [origData, setOrigData] = useState<string | null>(null);
  const [nat, setNat] = useState({ w: 0, h: 0 });
  const [aspect, setAspect] = useState<number | null>(null);
  const [rect, setRect] = useState<Rect>({ x: 0, y: 0, w: 0, h: 0 });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const dragRef = useRef<Drag | null>(null);
  const scale = nat.w > 0 && nat.h > 0 ? Math.min(560 / nat.w, 400 / nat.h) : 0;
  const scaleRef = useRef(scale);
  scaleRef.current = scale;

  useEffect(() => {
    if (!open) return;
    setOrigData(null);
    setNat({ w: 0, h: 0 });
    setAspect(null);
    setRect({ x: 0, y: 0, w: 0, h: 0 });
    setErr(null);
    setBusy(false);
  }, [open]);

  useEffect(() => {
    const move = (e: PointerEvent) => {
      const d = dragRef.current;
      if (!d) return;
      const sc = scaleRef.current || 1;
      const dx = (e.clientX - d.sx) / sc;
      const dy = (e.clientY - d.sy) / sc;
      if (d.mode === "move") {
        setRect((r) => ({
          ...r,
          x: clamp(d.orig.x + dx, 0, d.nat.w - d.orig.w),
          y: clamp(d.orig.y + dy, 0, d.nat.h - d.orig.h),
        }));
        return;
      }
      const o = d.orig;
      const ax = d.anchor?.x ?? 0;
      const ay = d.anchor?.y ?? 0;
      const rawW = d.horiz === "right" ? o.w + dx : o.w - dx;
      const rawH = d.vert === "bottom" ? o.h + dy : o.h - dy;
      const capW = d.horiz === "right" ? d.nat.w - ax : ax;
      const capH = d.vert === "bottom" ? d.nat.h - ay : ay;
      let w = clamp(rawW, 40, capW);
      let h = d.aspect ? w / d.aspect : clamp(rawH, 40, capH);
      if (d.aspect) {
        if (h > capH) {
          h = capH;
          w = h * d.aspect;
        }
        if (w > capW) {
          w = capW;
          h = w / d.aspect;
        }
      }
      setRect({
        x: d.horiz === "right" ? ax : ax - w,
        y: d.vert === "bottom" ? ay : ay - h,
        w,
        h,
      });
    };
    const up = () => {
      dragRef.current = null;
    };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
    return () => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
    };
  }, []);

  const loadFile = async (file: File) => {
    try {
      const data = await readAsDataUrl(file);
      const img = await loadImage(data);
      const a = defaultAspect ?? null;
      setOrigData(data);
      setNat({ w: img.naturalWidth, h: img.naturalHeight });
      setAspect(a);
      setRect(rectFor(img.naturalWidth, img.naturalHeight, a));
      setErr(null);
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    }
  };

  const chooseAspect = (a: number | null) => {
    if (!nat.w || !nat.h) return;
    setAspect(a);
    setRect(rectFor(nat.w, nat.h, a));
  };

  const startCorner = (mode: Corner, e: ReactPointerEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const o = rect;
    const right = mode === "se" || mode === "ne";
    const bottom = mode === "se" || mode === "sw";
    dragRef.current = {
      mode,
      sx: e.clientX,
      sy: e.clientY,
      orig: o,
      aspect,
      nat,
      anchor: { x: right ? o.x : o.x + o.w, y: bottom ? o.y : o.y + o.h },
      horiz: right ? "right" : "left",
      vert: bottom ? "bottom" : "top",
    };
  };

  const confirm = async () => {
    if (!origData) return;
    setBusy(true);
    setErr(null);
    try {
      const img = await loadImage(origData);
      const shrink = Math.min(1, CROP_MAX_SIDE / Math.max(rect.w, rect.h));
      const canvas = document.createElement("canvas");
      canvas.width = Math.max(1, Math.round(rect.w * shrink));
      canvas.height = Math.max(1, Math.round(rect.h * shrink));
      const ctx = canvas.getContext("2d");
      if (!ctx) throw new Error("浏览器不支持图片处理");
      ctx.drawImage(img, rect.x, rect.y, rect.w, rect.h, 0, 0, canvas.width, canvas.height);
      const url = await upload(await toBase64Under(canvas));
      onDone(url);
      onOpenChange(false);
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  const masks = [
    { top: 0, left: 0, width: "100%", height: rect.y * scale },
    { top: (rect.y + rect.h) * scale, left: 0, width: "100%", height: "100%" },
    { top: rect.y * scale, left: 0, width: rect.x * scale, height: rect.h * scale },
    { top: rect.y * scale, left: (rect.x + rect.w) * scale, width: "100%", height: rect.h * scale },
  ];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>
        {err ? (
          <p role="alert" className="text-sm text-destructive">
            {err}
          </p>
        ) : null}
        {!nat.w ? (
          <div className="flex flex-col items-center gap-3 py-6">
            <p className="text-sm text-muted-foreground">选择一张图,再框选要展示的部分。</p>
            <Button variant="outline" onClick={() => pickRef.current?.click()}>
              选择图片
            </Button>
            <input
              ref={pickRef}
              type="file"
              accept="image/*"
              aria-label="选择图片文件"
              className="hidden"
              onChange={(e) => {
                const f = e.target.files?.[0];
                e.target.value = "";
                if (f) void loadFile(f);
              }}
            />
          </div>
        ) : (
          <>
            <div className="flex flex-wrap items-center gap-1.5">
              {PRESETS.map((p) => (
                <Button
                  key={p.label}
                  size="sm"
                  variant={aspect === p.value ? "default" : "outline"}
                  onClick={() => chooseAspect(p.value)}
                >
                  {p.label}
                </Button>
              ))}
              <span className="ml-1 text-xs text-muted-foreground">
                拖动选框移动;四角拖拽缩放(锁定比例)。
              </span>
            </div>
            <div
              className="relative mx-auto overflow-hidden rounded border bg-black/20"
              style={{ width: nat.w * scale, height: nat.h * scale }}
            >
              {origData ? (
                <img
                  src={origData}
                  alt="裁剪源图"
                  className="block select-none"
                  style={{ width: nat.w * scale, height: nat.h * scale }}
                  draggable={false}
                />
              ) : null}
              {masks.map((m, i) => (
                <div key={i} className="pointer-events-none absolute bg-black/45" style={m} />
              ))}
              <div
                className="absolute cursor-move border-2 border-primary"
                style={{
                  left: rect.x * scale,
                  top: rect.y * scale,
                  width: rect.w * scale,
                  height: rect.h * scale,
                }}
                onPointerDown={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  dragRef.current = {
                    mode: "move",
                    sx: e.clientX,
                    sy: e.clientY,
                    orig: rect,
                    aspect,
                    nat,
                  };
                }}
              >
                {CORNERS.map((c) => (
                  <div
                    key={c.mode}
                    className="absolute h-3 w-3 rounded-sm border-2 border-primary bg-background"
                    style={{ ...c.pos, cursor: c.cursor }}
                    onPointerDown={(e) => startCorner(c.mode, e)}
                  />
                ))}
              </div>
            </div>
            <p className="text-xs text-muted-foreground">
              原图 {nat.w}×{nat.h}px · 裁剪区 {Math.round(rect.w)}×{Math.round(rect.h)}px
              {Math.max(rect.w, rect.h) > CROP_MAX_SIDE && ` · 上传时长边压到 ${CROP_MAX_SIDE}px`}
            </p>
            <DialogFooter>
              <Button variant="ghost" disabled={busy} onClick={() => onOpenChange(false)}>
                取消
              </Button>
              <Button disabled={busy || !origData} onClick={() => void confirm()}>
                {busy ? <Loader2 className="animate-spin" /> : null}裁好并上传
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
